import { TelephoneCountryCodesEnum } from '../../../../resources/base/models';
import { formatPhoneNumber, removeLeadingZerosFromMobilePhone } from '../../../../services/common/format';
import { DeepRequired } from '../../../../services/common/miscUtils.types';
import { genderToSalutation } from '../../utils/checkGenderUtil';
import { uiDateToApiDate } from '../../utils/formatDate';
import { getEmploymentsForRequestMapper } from '../utils';
import {
  AdditionalInfoDataFormType,
  AddressDataFormType,
  CompanyDataFormType,
  ExecutiveInfoDataFormType,
  GeneralInfoDataFormType,
  PaymentDataFormType,
  PersonalInfoDataFormType,
  SignatoryInfoDataFormType,
} from './initialValues';

type CurrentCreditApplicationData =
  | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
  | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

type UpdateCustomerDataRequest =
  | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataRequest
  | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataRequest;

export const generalInfoDataFormRequestMapper = (
  values: GeneralInfoDataFormType,
  currentCreditApplicationData: CurrentCreditApplicationData
): UpdateCustomerDataRequest => {
  const milesEmailId = currentCreditApplicationData.contactData?.emails?.[0].milesEmailId;
  const milesMobilePhoneNumberId = currentCreditApplicationData.contactData?.milesMobilePhoneNumberId;
  const milesTelephoneNumberId = currentCreditApplicationData.contactData?.milesTelephoneNumberId;

  return {
    customerType: 'LEGAL_ENTITY',
    companyData: {
      entityLegalForm: values.legalEntity as any,
      name: values.companyName,
      apecode: values.apecode,
      foundationDate: uiDateToApiDate(values.companyCreationDate),
    },
    contactData: {
      emails: values.email ? [{ emailAddress: values.email, milesEmailId }] : [],
      mobilePhoneCountryCode: values.mobilePhoneCountryCode,
      mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone),
      milesMobilePhoneNumberId,
      telephoneCountryCode: values.telephonePhone ? TelephoneCountryCodesEnum['+33'] : undefined,
      telephoneNumber: formatPhoneNumber(values.telephonePhone),
      milesTelephoneNumberId,
    },
  };
};

export const addressDataFormRequestMapper = (
  values: AddressDataFormType,
  currentCreditApplicationData: CurrentCreditApplicationData
): UpdateCustomerDataRequest => {
  const entityLegalForm = currentCreditApplicationData.companyData?.entityLegalForm;

  return {
    customerType: 'LEGAL_ENTITY',
    companyData: {
      entityLegalForm: entityLegalForm as any,
    },
    addresses: [
      {
        addressLine1: values.addressLine1,
        optionalLine: values.optionalLine,
        zipCode: values.zipCode,
        city: values.city,
        countryCode: values.country,
      },
    ],
  };
};

export const personalDataFormPaymentDataRequestMapper = (
  values: DeepRequired<PaymentDataFormType>
): CustomerIntegration.Schemas.PostAddBankAccountRequest => {
  return {
    customerData: {
      paymentData: {
        iban: values.iban,
        milesAccountHolder: values.bankAccountOwner,
        milesBankAccountOpeningDate: uiDateToApiDate(values.openingDate),
        bic: values.bic,
      },
    },
  };
};

export const personalDataFormRequestMapper = (
  values: DeepRequired<PersonalInfoDataFormType>,
  currentCreditApplicationData: CurrentCreditApplicationData
): UpdateCustomerDataRequest => {
  const entityLegalForm = currentCreditApplicationData.companyData?.entityLegalForm;

  return {
    customerType: 'LEGAL_ENTITY',
    companyData: {
      entityLegalForm: entityLegalForm as any,
    },
    customerRelations: [
      {
        role: 'CEO',
        gender: values.gender,
        salutation: genderToSalutation(values.gender),
        firstName: values.ceoFirstName,
        lastName: values.ceoLastName,
        birthName: values.ceoBirthName,
        dateOfBirth: uiDateToApiDate(values.ceoDateOfBirth),
        nationality: values.ceoNationality,
        countryOfBirth: values.ceoBirthCountry,
        placeOfBirthZipCode: values.ceoBirthPlaceZip,
        placeOfBirth: values.ceoBirthPlaceCity,
      },
    ],
  };
};

export const additionalInfoDataFormRequestMapper = (
  values: AdditionalInfoDataFormType,
  currentCreditApplicationData: CurrentCreditApplicationData
): CreditIntegration.Schemas.UpdateSoleTraderCustomerDataRequest => {
  const entityLegalForm = currentCreditApplicationData.companyData?.entityLegalForm;

  return {
    customerType: 'LEGAL_ENTITY',
    companyData: {
      entityLegalForm: entityLegalForm as any,
    },
    customerRelations: [
      {
        role: 'CEO',
        employments: [
          {
            posFrOccupation: values.professionEntrepreneur!,
          },
        ],
        financialDisclosure: {
          housingSituation: values.residenceStatus,
          incomes: [
            {
              incomeType: 'PERSONAL_INCOME',
              amount: { netAmount: values.netIncomeAmount },
            },
            {
              incomeType: 'ADDITIONAL',
              amount: { netAmount: values.otherIncome },
            },
          ],
          expenses: [
            {
              personal: [
                {
                  expenseType: 'RENT',
                  amount: { netAmount: values.monthlyPropertyCharge },
                },
                {
                  expenseType: 'ADDITIONAL',
                  amount: { netAmount: values.otherMonthlyCosts },
                },
              ],
            },
          ],
        },
        maritalStatus: values.maritalStatus,
        numberOfDependentChildren: values.numberOfDependentChildren,
      },
    ],
  };
};

export const companyDataFormRequestMapper = (
  values: CompanyDataFormType,
  currentCreditApplicationData:
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse
): UpdateCustomerDataRequest => {
  const entityLegalForm = currentCreditApplicationData.companyData?.entityLegalForm;

  return {
    customerType: 'LEGAL_ENTITY',
    companyData: {
      entityLegalForm: entityLegalForm as any,
    },
    employments: getEmploymentsForRequestMapper(values, currentCreditApplicationData),
  };
};

const basicStakeholderInfoDataFormRequestMapper = (
  values: ExecutiveInfoDataFormType,
  milesMobilePhoneNumberId?: string,
  milesEmailId?: string
): CustomerIntegration.Schemas.UpdateStakeholderContactsRequest => {
  return {
    firstName: values.ceoFirstName,
    lastName: values.ceoLastName,
    birthName: values.ceoBirthName,
    dateOfBirth: uiDateToApiDate(values.ceoDateOfBirth),
    nationality: values.ceoNationality,
    gender: values.gender,
    salutation: values.gender ? genderToSalutation(values.gender) : undefined,
    countryOfBirth: values.ceoBirthCountry,
    placeOfBirth: values.ceoBirthPlaceCity,
    placeOfBirthZipCode: values.ceoBirthPlaceZip ?? undefined,
    contactData: {
      emails: values.email ? [{ emailAddress: values.email, milesEmailId }] : [],
      mobilePhoneCountryCode: values.mobilePhoneCountryCode as CreditIntegration.Schemas.PhoneCountryCode,
      mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone ?? undefined),
      milesMobilePhoneNumberId,
    },
  };
};

export const executiveInfoDataFormUpdateRequestMapper = (
  values: ExecutiveInfoDataFormType,
  milesMobilePhoneNumberId?: string,
  milesEmailId?: string
) => basicStakeholderInfoDataFormRequestMapper(values, milesMobilePhoneNumberId, milesEmailId);

export const executiveInfoDataFormCreateRequestMapper = (
  values: ExecutiveInfoDataFormType
): CustomerIntegration.Schemas.CustomerDataCreateStakeholderContactsRequest => {
  const payload = basicStakeholderInfoDataFormRequestMapper(
    values
  ) as CustomerIntegration.Schemas.CustomerDataCreateStakeholderContactsRequest;
  payload.milesType = 'new';
  payload.milesStakeholderContactTypes = ['DIRECTOR', 'SIGNATORY'];
  return payload;
};

export const signatoryInfoDataFormUpdateRequestMapper = (
  values: SignatoryInfoDataFormType,
  milesMobilePhoneNumberId?: string,
  milesEmailId?: string
) => basicStakeholderInfoDataFormRequestMapper(values, milesMobilePhoneNumberId, milesEmailId);

export const signatoryInfoDataFormCreateRequestMapper = (
  values: SignatoryInfoDataFormType
): CustomerIntegration.Schemas.CustomerDataCreateStakeholderContactsRequest => {
  const payload = basicStakeholderInfoDataFormRequestMapper(
    values
  ) as CustomerIntegration.Schemas.CustomerDataCreateStakeholderContactsRequest;
  payload.milesType = 'new';
  payload.milesStakeholderContactTypes = ['SIGNATORY'];
  return payload;
};

export const paymentDataFormRequestMapper = (
  values: PaymentDataFormType
): CustomerIntegration.Schemas.PostAddBankAccountRequest => {
  return {
    customerData: {
      paymentData: {
        iban: values.iban!,
        milesAccountHolder: values.bankAccountOwner,
        milesBankAccountOpeningDate: uiDateToApiDate(values.openingDate)!,
        bic: values.bic,
      },
    },
  };
};
